import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReunionStore } from '../store/reunionStore';
import { api } from '../api/client';
import { PageLoader } from '../components/PageLoader';
import { config } from '../config';
import type { ReunionDetail, ReunionSummary } from '../types/reunion';
import { DEFAULT_FEATURE_TOGGLES } from '../types/reunion';

interface ReunionContextValue {
  activeReunionId: string | null;
  reunion: ReunionDetail | null;
  isLoading: boolean;
  setActiveReunionId: (id: string | null) => void;
}

const ReunionContext = createContext<ReunionContextValue>({
  activeReunionId: null,
  reunion: null,
  isLoading: false,
  setActiveReunionId: () => {},
});

export function ReunionProvider({ children }: { children: ReactNode }) {
  const { activeReunionId, setActiveReunionId } = useReunionStore();
  const [resolved, setResolved] = useState(false);

  // Auto-resolve: fetch the default reunion by slug and set it as active
  useEffect(() => {
    if (activeReunionId) {
      setResolved(true);
      return;
    }

    let cancelled = false;
    const slug = config.defaultReunionSlug;

    api.get<{ data: ReunionSummary[] }>('/reunions')
      .then((res) => {
        if (cancelled) return;
        const match = res.data.find((r) => r.slug === slug);
        if (match) {
          setActiveReunionId(match.id);
        }
        setResolved(true);
      })
      .catch(() => {
        if (!cancelled) setResolved(true);
      });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useQuery({
    queryKey: ['reunion', activeReunionId],
    queryFn: () => api.get<{ data: ReunionDetail }>(`/reunions/${activeReunionId}`),
    enabled: !!activeReunionId && resolved,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    api.setReunionId(activeReunionId);
  }, [activeReunionId]);

  if (!resolved) {
    return <PageLoader />;
  }

  return (
    <ReunionContext.Provider value={{
      activeReunionId,
      reunion: data?.data
        ? { ...data.data, featureToggles: { ...DEFAULT_FEATURE_TOGGLES, ...data.data.featureToggles } }
        : null,
      isLoading,
      setActiveReunionId,
    }}>
      {children}
    </ReunionContext.Provider>
  );
}

export function useReunion() {
  return useContext(ReunionContext);
}
